import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  loadImages,
  loadActivities,
  DEFAULT_IMAGE_SLOTS,
  DEFAULT_ACTIVITIES,
  type ImageMap,
  type ActivityCard,
} from '@/lib/store';

interface SiteImagesValue {
  /** Named section slots (logo/hero/lake/about) resolved to live URLs. */
  images: ImageMap;
  /** Editable activity cards (with their images). */
  activities: ActivityCard[];
  /** (keyof images) -> url, with safe default fallback. */
  img: (slot: keyof ImageMap) => string;
  /** Re-fetch the live data (e.g. after an admin publish). */
  refresh: () => void;
}

const SiteImagesContext = createContext<SiteImagesValue | null>(null);

export function SiteImagesProvider({ children }: { children: ReactNode }) {
  const [images, setImages] = useState<ImageMap>(() => ({ ...DEFAULT_IMAGE_SLOTS }));
  const [activities, setActivities] = useState<ActivityCard[]>(() => [...DEFAULT_ACTIVITIES]);

  const refresh = useCallback(() => {
    loadImages()
      .then((im) => setImages((prev) => ({ ...DEFAULT_IMAGE_SLOTS, ...prev, ...im })))
      .catch(() => {});
    loadActivities()
      .then((acts) => {
        if (acts && acts.length) setActivities(acts);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const onPublish = () => refresh();
    window.addEventListener('chkz:published', onPublish);
    return () => window.removeEventListener('chkz:published', onPublish);
  }, [refresh]);

  const img = useCallback(
    (slot: keyof ImageMap) => images[slot] ?? DEFAULT_IMAGE_SLOTS[slot] ?? '',
    [images]
  );

  return (
    <SiteImagesContext.Provider value={{ images, activities, img, refresh }}>
      {children}
    </SiteImagesContext.Provider>
  );
}

export function useSiteImages(): SiteImagesValue {
  const ctx = useContext(SiteImagesContext);
  if (!ctx) {
    // Fallback when used outside the provider (e.g. tests / partial renders).
    return {
      images: { ...DEFAULT_IMAGE_SLOTS },
      activities: [...DEFAULT_ACTIVITIES],
      img: (slot) => DEFAULT_IMAGE_SLOTS[slot] ?? '',
      refresh: () => {},
    };
  }
  return ctx;
}
